import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { DataService, Update, QueryParams } from './entity-manager.model';
import { API_PREFIX } from './constants';
import { CollectionManagerService, EntityUrlPattern, UrlPatternOperation } from './collection-manager.service';

export class DefaultDataService<T> implements DataService<T> {
  protected _name: string;

  get name() {
    return this._name;
  }

  constructor(
    public entityName: string,
    private _http: HttpClient,
    private _apiPrefix: string,
    private _urlPattern?: EntityUrlPattern
  ) {
    this._name = `${entityName} DefaultDataService`;
    this.entityName = entityName;
  }

  add(entity: T, meta?: any) {
    const url = this._buildUrl('add', entity, meta);
    return this._http.post<T>(url, entity);
  }

  delete(id: string, meta?: any) {
    const url = this._buildUrl('delete', null, meta, id);
    return this._http.delete<string>(url);
  }

  // TODO: interface
  getAll(data: any = {}, meta?: any) {
    const url = this._buildUrl('getAll', data, meta);
    return this._http.post<T[]>(
      url,
      // TODO: temporary -> REMOVE
      {
        search: [{ ...data.search[0], type: 'match' }],
        sort: []
      }
    );
  }

  // TODO: interface
  searchAll(data: any = {}, meta?: any) {
    const url = this._buildUrl('search', data, meta);
    return this._http.post<{ items: T[] }>(url, data);
  }

  getById(id: string, meta?: any) {
    const url = this._buildUrl('getById', null, meta, id);
    return this._http.get<T>(url);
  }

  getWithQuery(queryParams: QueryParams | string, meta?: any) {
    const url = this._buildUrl('getWithQuery', null, meta);
    const qParams = typeof queryParams === 'string'
      ? { fromString: queryParams }
      : { fromObject: queryParams };
    const params = new HttpParams(qParams);

    return this._http.get<T[]>(url, { params });
  }

  update(update: Update<T>, meta?: any) {
    const url = this._buildUrl('update', update.changes, meta, update.id);
    return this._http.put<T>(url, update.changes);
  }

  restore(id: string, meta?: any) {
    const url = this._buildUrl('restore', null, meta, id);
    return this._http.put<T>(url, {});
  }

  private _buildUrl(
    operation: UrlPatternOperation,
    entity?: any,
    meta?: any,
    id?: string
  ): string {
    if (!this._urlPattern) {
      return this._buildFlatUrl(operation, id);
    }

    const operationConfig = this._urlPattern.operations?.[operation] ?? this._urlPattern.default;

    if (operationConfig === 'flat') {
      return this._buildFlatUrl(operation, id);
    }

    const parentIds = operationConfig.getParentIds(entity, meta);

    const missingIds = Object.entries(parentIds).filter(([ _, value ]) => !value);
    if (missingIds.length > 0) {
      console.warn(
        `[DefaultDataService] Missing parent IDs for nested route on entity '${this.entityName}', ` +
        `operation '${operation}', falling back to flat URL. Missing: ${missingIds.map(([ k ]) => k).join(', ')}`
      );
      return this._buildFlatUrl(operation, id);
    }

    const url = Object.entries(parentIds).reduce((acc, [ key, value ]) => {
      return acc.replace(`:${key}`, value);
    }, operationConfig.pattern);

    return `${this._apiPrefix}/${url}${this._getOperationSuffix(operation, id)}`;
  }

  private _buildFlatUrl(operation: string, id?: string): string {
    const base = `${this._apiPrefix}/${this.entityName}`;
    switch (operation) {
      case 'add':
      case 'getWithQuery':
        return base;
      case 'getAll':
        return `${base}/list`;
      case 'search':
        return `${base}/search`;
      case 'getById':
      case 'update':
      case 'delete':
        return `${base}/${id}`;
      case 'restore':
        return `${base}/${id}/restore`;
      default:
        return base;
    }
  }

  private _getOperationSuffix(operation: string, id?: string): string {
    switch (operation) {
      case 'add':
      case 'getAll':
      case 'getWithQuery':
        return '';
      case 'search':
        return '/search';
      case 'getById':
      case 'update':
      case 'delete':
        return `/${id}`;
      case 'restore':
        return `/${id}/restore`;
      default:
        return '';
    }
  }
}

@Injectable({ providedIn: 'root' })
export class DefaultDataServiceFactory {
  #http = inject(HttpClient);
  #apiPrefix = inject(API_PREFIX);
  #collectionManager = inject(CollectionManagerService);

  create<T>(entityName: string): DataService<T> {
    const urlPattern = this.#collectionManager.getUrlPattern(entityName);
    return new DefaultDataService<T>(entityName, this.#http, this.#apiPrefix, urlPattern);
  }
}
